import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter, BadgeCheck } from 'lucide-react';

const footerLinks = {
  services: [
    { name: 'General Consultations', href: '/services' },
    { name: 'Health Assessments', href: '/services' },
    { name: 'Diagnosis & Treatment', href: '/services' },
    { name: 'Preventive Care', href: '/services' },
    { name: 'Minor Surgical Procedures', href: '/services' },
  ],
  company: [
    { name: 'About Us', href: '/about' },
    { name: 'Careers', href: '/careers' },
    { name: 'Resources', href: '/resources' },
    { name: 'Contact', href: '/contact' },
  ],
  legal: [
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Terms of Service', href: '/terms' },
    { name: 'HIPAA Compliance', href: '/hipaa' },
  ],
};

export const Footer = () => {
  return (
    <footer className="bg-navy text-white" data-testid="footer">
      <div className="container-custom section-padding">
        {/* Main footer content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 lg:gap-8">
          {/* Company info */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-white rounded-sm flex items-center justify-center">
                <span className="text-navy font-manrope font-bold text-lg">HC</span>
              </div>
              <div>
                <span className="font-manrope font-bold text-white text-lg tracking-tight">HealthCheck</span>
                <span className="font-manrope font-medium text-emerald-100 text-lg"> Medical</span>
              </div>
            </div>
            <p className="text-navy-200 text-sm leading-relaxed mb-6 max-w-sm">
              HealthCheck Medical Inc. provides comprehensive medical and surgical services with safe, professional, and compassionate care tailored to each patient's needs.
            </p>
            <div className="space-y-3">
              <a href="tel:+18558311992" className="flex items-center gap-3 text-navy-200 hover:text-white transition-colors" data-testid="footer-phone">
                <Phone className="w-4 h-4" />
                <span className="text-sm">(855) 831-1992</span>
              </a>
              <a href="mailto:info@healthcentermedicalgroup.online" className="flex items-center gap-3 text-navy-200 hover:text-white transition-colors" data-testid="footer-email">
                <Mail className="w-4 h-4" />
                <span className="text-sm">info@healthcentermedicalgroup.online</span>
              </a>
              <div className="flex items-start gap-3 text-navy-200">
                <MapPin className="w-4 h-4 mt-0.5" />
                <div className="text-sm">
                  <div>Main Office: 7100 Commerce Way Ste 182</div>
                  <div>Brentwood, TN 37027</div>
                </div>
              </div>
              <a 
                href="https://npiregistry.cms.hhs.gov/provider-view/1982516498" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-navy-200 hover:text-emerald transition-colors group"
                data-testid="footer-npi"
              >
                <BadgeCheck className="w-4 h-4" />
                <span className="text-sm">
                  NPI: <span className="underline group-hover:text-emerald">1982516498</span>
                </span>
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-manrope font-semibold text-white mb-4">Services</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href} 
                    className="text-sm text-navy-200 hover:text-white transition-colors"
                    data-testid={`footer-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-manrope font-semibold text-white mb-4">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href} 
                    className="text-sm text-navy-200 hover:text-white transition-colors"
                    data-testid={`footer-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-manrope font-semibold text-white mb-4">Legal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href} 
                    className="text-sm text-navy-200 hover:text-white transition-colors"
                    data-testid={`footer-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-8 border-t border-navy-700 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-navy-300">
            © {new Date().getFullYear()} HealthCheck Medical Inc. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a 
              href="https://linkedin.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-sm bg-navy-700 flex items-center justify-center hover:bg-emerald transition-colors"
              data-testid="footer-linkedin"
            >
              <Linkedin className="w-4 h-4" />
            </a>
            <a 
              href="https://twitter.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-sm bg-navy-700 flex items-center justify-center hover:bg-emerald transition-colors"
              data-testid="footer-twitter"
            >
              <Twitter className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
